import {Box, Checkbox, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography} from "@mui/material";
import React from "react";

interface PermissionsSelectorProps {
  permissions: Record<string, Record<string, boolean>>;
  setPermissions: React.Dispatch<React.SetStateAction<Record<string, Record<string, boolean>>>>;
  /** When true, checkboxes are disabled (e.g. view-only user profile). */
  readOnly?: boolean;
}

/**
 * What every signed-in user can do whatever their permissions. These are not stored permissions:
 * the backend serves them to any signed-in user (own data only, tenant-filtered), so the row is
 * informational. Keep in sync with the permission-free routes in flock-be (`internal/users/router.go`
 * /me routes, `GET /api/analytic-profiles`, `POST /api/feedback/general`, `/api/auth/*`); see
 * flock-be#52 and flock-be#53 for the audit that settled this list.
 */
export const BASIC_CAPABILITIES = [
  'Sign in and reset a forgotten password',
  'See their own account and Home',
  'Change their own password and profile picture',
  'Pick among the analytic profiles assigned to them',
  'Send feedback',
];

const sections = {
  users: ["create", "read", "update", "delete"],
  company_profile: ["read", "update"],
  analytic_profiles: ["create", "read", "update", "delete"],
  notebooks: ["create", "read", "update", "delete"],
  podcasts: ["create", "read", "update", "delete"],
  clusters: ["create", "read", "update", "delete"],
  images: ["create", "read", "update", "delete"],
  content_radar: ["read"],
  social_studio: ["create", "read", "update"],
  idea_sparker: ["create", "read"],
  social_listening: ["read", "update"]
};

const allActions = Array.from(new Set(Object.values(sections).flat()));

export default function PermissionsSelector({
  permissions,
  setPermissions,
  readOnly = false,
}: PermissionsSelectorProps) {
  const togglePermission = (section: keyof typeof sections, permission: string) => {
    if (readOnly) return;
    setPermissions((prev) => ({
      ...prev,
      [section]: {
        ...(prev[section] || {}),
        [permission]: !(prev[section]?.[permission] || false)
      }
    }));
  };

  return (
    <TableContainer 
      component={Paper}
      sx={{
        bgcolor: '#0a0a0a',
        border: '1px solid #27272a'
      }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: '#0a0a0a', borderBottom: '2px solid #27272a' }}>
            <TableCell sx={{ color: '#fafafa', borderColor: '#27272a' }}>Section</TableCell>
            <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a' }}>All</TableCell>
            {allActions.map((perm) => (
              <TableCell key={perm} align="center" sx={{ color: '#fafafa', borderColor: '#27272a' }}>
                {perm.charAt(0).toUpperCase() + perm.slice(1)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ backgroundColor: '#0a0a0a' }} data-testid="permissions-basics-row">
            <TableCell sx={{ color: '#a1a1aa', borderColor: '#27272a' }}>
              Basics
              <Typography component="div" variant="caption" sx={{ color: '#71717a' }}>
                Always included
              </Typography>
            </TableCell>
            <TableCell align="center" sx={{ borderColor: '#27272a' }}>
              <Checkbox
                checked
                disabled
                inputProps={{ 'aria-label': 'Basics (always included)' }}
                sx={{ '&.Mui-disabled.Mui-checked': { color: '#52525b' } }}
              />
            </TableCell>
            <TableCell colSpan={allActions.length} sx={{ color: '#71717a', borderColor: '#27272a', fontSize: '0.8125rem' }}>
              <Box component="ul" aria-label="Always included" sx={{ m: 0, pl: 2.25 }}>
                {BASIC_CAPABILITIES.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </Box>
            </TableCell>
          </TableRow>
          {Object.entries(sections).map(([section, perms]) => {
            const typedSection = section as keyof typeof sections;
            const sectionPerms = permissions[typedSection] || {};
            const checkedValues = perms.map((perm) => sectionPerms[perm]);
            const allChecked = checkedValues.every(Boolean);
            const someChecked = checkedValues.some(Boolean);
            const sectionLabel = section.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

            return (
              <TableRow 
                key={section}
                sx={{ 
                  backgroundColor: '#0a0a0a',
                  '&:hover': { backgroundColor: '#1a1a1a' }
                }}
              >
                <TableCell sx={{ color: '#fafafa', borderColor: '#27272a' }}>
                  {sectionLabel}
                </TableCell>
                <TableCell align="center" sx={{ borderColor: '#27272a' }}>
                  <Checkbox
                    disabled={readOnly}
                    checked={allChecked}
                    indeterminate={!allChecked && someChecked}
                    inputProps={{ 'aria-label': `${sectionLabel}: all` }}
                    onChange={() => {
                      if (readOnly) return;
                      const newState = !allChecked;
                      setPermissions((prev) => ({
                        ...prev,
                        [typedSection]: perms.reduce((acc, perm) => {
                          acc[perm] = newState;
                          return acc;
                        }, {} as Record<string, boolean>)
                      }));
                    }}
                    sx={{ 
                      color: '#a1a1aa', 
                      '&.Mui-checked': { color: '#60a5fa' },
                      '&.MuiCheckbox-indeterminate': { color: '#60a5fa' }
                    }}
                  />
                </TableCell>
                {allActions.map((perm) => (
                  <TableCell key={perm} align="center" sx={{ borderColor: '#27272a' }}>
                    {perms.includes(perm) ? (
                      <Checkbox
                        disabled={readOnly}
                        checked={permissions[typedSection]?.[perm] || false}
                        onChange={() => togglePermission(typedSection, perm)}
                        inputProps={{ 'aria-label': `${sectionLabel}: ${perm}` }}
                        sx={{ 
                          color: '#a1a1aa', 
                          '&.Mui-checked': { color: '#60a5fa' }
                        }}
                      />
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
